import React, { useState } from "react";
import {
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Alert,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English" },
  { code: "hi", name: "हिंदी (Hindi)" },
  { code: "hn", name: "Hinglish" },
  { code: "ta", name: "தமிழ் (Tamil)" },
  { code: "te", name: "తెలుగు (Telugu)" },
  { code: "kn", name: "ಕನ್ನಡ (Kannada)" },
  { code: "bn", name: "বাংলা (Bengali)" },
  { code: "gu", name: "ગુજરાતી (Gujarati)" },
  { code: "mr", name: "मराठी (Marathi)" },
];

const RoleManager = ({ open, onClose, onCreateRole, editingRole = null, onUpdateRole }) => {
  const [roleData, setRoleData] = useState({
    name: "",
    slug: "",
    metaTitle: "",
    metaDescription: "",
    ogTitle: "",
    ogDescription: "",
    ogImage: "",
    twitterTitle: "",
    twitterDescription: "",
    heading: "",
    heading_hi: "",
    heading_hn: "",
    heading_ta: "",
    heading_te: "",
    heading_kn: "",
    heading_bn: "",
    heading_gu: "",
    heading_mr: "",
    subheading: "",
    subheading_hi: "",
    subheading_hn: "",
    subheading_ta: "",
    subheading_te: "",
    subheading_kn: "",
    subheading_bn: "",
    subheading_gu: "",
    subheading_mr: "",
    paragraph: "",
    paragraph_hi: "",
    paragraph_hn: "",
    paragraph_ta: "",
    paragraph_te: "",
    paragraph_kn: "",
    paragraph_bn: "",
    paragraph_gu: "",
    paragraph_mr: "",
  });
  const [error, setError] = useState("");
  const [currentLanguageTab, setCurrentLanguageTab] = useState("en");

  // Load editing role data when component opens in edit mode
  React.useEffect(() => {
    if (editingRole) {
      setRoleData({
        name: editingRole.name || "",
        slug: editingRole.slug || "",
        metaTitle: editingRole.metaTitle || "",
        metaDescription: editingRole.metaDescription || "",
        ogTitle: editingRole.ogTitle || "",
        ogDescription: editingRole.ogDescription || "",
        ogImage: editingRole.ogImage || "",
        twitterTitle: editingRole.twitterTitle || "",
        twitterDescription: editingRole.twitterDescription || "",
        heading: editingRole.heading || "",
        heading_hi: editingRole.heading_hi || "",
        heading_hn: editingRole.heading_hn || "",
        heading_ta: editingRole.heading_ta || "",
        heading_te: editingRole.heading_te || "",
        heading_kn: editingRole.heading_kn || "",
        heading_bn: editingRole.heading_bn || "",
        heading_gu: editingRole.heading_gu || "",
        heading_mr: editingRole.heading_mr || "",
        subheading: editingRole.subheading || "",
        subheading_hi: editingRole.subheading_hi || "",
        subheading_hn: editingRole.subheading_hn || "",
        subheading_ta: editingRole.subheading_ta || "",
        subheading_te: editingRole.subheading_te || "",
        subheading_kn: editingRole.subheading_kn || "",
        subheading_bn: editingRole.subheading_bn || "",
        subheading_gu: editingRole.subheading_gu || "",
        subheading_mr: editingRole.subheading_mr || "",
        paragraph: editingRole.paragraph || "",
        paragraph_hi: editingRole.paragraph_hi || "",
        paragraph_hn: editingRole.paragraph_hn || "",
        paragraph_ta: editingRole.paragraph_ta || "",
        paragraph_te: editingRole.paragraph_te || "",
        paragraph_kn: editingRole.paragraph_kn || "",
        paragraph_bn: editingRole.paragraph_bn || "",
        paragraph_gu: editingRole.paragraph_gu || "",
        paragraph_mr: editingRole.paragraph_mr || "",
      });
    }
  }, [editingRole]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setRoleData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Auto-generate slug from name
    if (name === "name") {
      setRoleData((prev) => ({
        ...prev,
        slug: value
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[^\w-]/g, ""),
      }));
    }
  };

  const validateForm = () => {
    if (!roleData.name.trim()) {
      setError("Role name is required");
      return false;
    }
    if (!roleData.slug.trim()) {
      setError("Slug is required");
      return false;
    }
    if (!roleData.metaTitle.trim()) {
      setError("Meta title is required");
      return false;
    }
    if (!roleData.metaDescription.trim()) {
      setError("Meta description is required");
      return false;
    }
    return true;
  };

  const handleSubmit = () => {
    setError("");
    if (!validateForm()) {
      return;
    }
    if (editingRole) {
      onUpdateRole(editingRole._id, roleData);
    } else {
      onCreateRole(roleData);
    }
    // Do not reset fields on submit
  };

  const handleClose = () => {
    setError("");
    setRoleData({
      name: "",
      slug: "",
      metaTitle: "",
      metaDescription: "",
      ogTitle: "",
      ogDescription: "",
      ogImage: "",
      twitterTitle: "",
      twitterDescription: "",
      heading: "",
      heading_hi: "",
      heading_hn: "",
      heading_ta: "",
      heading_te: "",
      heading_kn: "",
      heading_bn: "",
      heading_gu: "",
      heading_mr: "",
      subheading: "",
      subheading_hi: "",
      subheading_hn: "",
      subheading_ta: "",
      subheading_te: "",
      subheading_kn: "",
      subheading_bn: "",
      subheading_gu: "",
      subheading_mr: "",
      paragraph: "",
      paragraph_hi: "",
      paragraph_hn: "",
      paragraph_ta: "",
      paragraph_te: "",
      paragraph_kn: "",
      paragraph_bn: "",
      paragraph_gu: "",
      paragraph_mr: "",
    });
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        // Only close if Cancel button is clicked (reason will be undefined)
        // Block closing from backdrop (reason = "backdropClick")
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{editingRole ? "Edit Role" : "Create New Role"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* Basic Info */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Basic Information
            </Typography>
            <TextField
              fullWidth
              label="Role Name"
              name="name"
              value={roleData.name}
              onChange={handleInputChange}
              placeholder="e.g., Rider, Driver"
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Slug URL"
              name="slug"
              value={roleData.slug}
              onChange={handleInputChange}
              placeholder="e.g., rider, driver"
              size="small"
              helperText="Auto-generated from role name"
            />
          </Box>

          <Divider />

          {/* SEO Meta Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              SEO Metadata
            </Typography>
            <TextField
              fullWidth
              label="Meta Title"
              name="metaTitle"
              value={roleData.metaTitle}
              onChange={handleInputChange}
              placeholder="Page title for search engines"
              size="small"
              sx={{ mb: 1 }}
              helperText="Recommended: 50-60 characters"
            />
            <TextField
              fullWidth
              label="Meta Description"
              name="metaDescription"
              value={roleData.metaDescription}
              onChange={handleInputChange}
              placeholder="Page description for search engines"
              size="small"
              multiline
              rows={2}
              sx={{ mb: 1 }}
              helperText="Recommended: 150-160 characters"
            />
          </Box>

          <Divider />

          {/* Multilingual Content */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Dynamic Content (Multilingual)
            </Typography>

            <Tabs
              value={currentLanguageTab}
              onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Tab key={lang.code} label={lang.name} value={lang.code} />
              ))}
            </Tabs>

            <TextField
              fullWidth
              label={`Heading (${SUPPORTED_LANGUAGES.find(l => l.code === currentLanguageTab)?.name})`}
              name={currentLanguageTab === "en" ? "heading" : `heading_${currentLanguageTab}`}
              value={currentLanguageTab === "en" ? roleData.heading : roleData[`heading_${currentLanguageTab}`]}
              onChange={handleInputChange}
              // placeholder="Main heading for the role"
              size="small"
              sx={{ mb: 1 }}
            />

            <TextField
              fullWidth
              label={`Subheading (${SUPPORTED_LANGUAGES.find(l => l.code === currentLanguageTab)?.name})`}
              name={currentLanguageTab === "en" ? "subheading" : `subheading_${currentLanguageTab}`}
              value={currentLanguageTab === "en" ? roleData.subheading : roleData[`subheading_${currentLanguageTab}`]}
              onChange={handleInputChange}
              // placeholder="Subheading for the role"
              size="small"
              sx={{ mb: 1 }}
            />

            <TextField
              fullWidth
              label={`Paragraph (${SUPPORTED_LANGUAGES.find(l => l.code === currentLanguageTab)?.name})`}
              name={currentLanguageTab === "en" ? "paragraph" : `paragraph_${currentLanguageTab}`}
              value={currentLanguageTab === "en" ? roleData.paragraph : roleData[`paragraph_${currentLanguageTab}`]}
              onChange={handleInputChange}
              // placeholder="Detailed description paragraph"
              size="small"
              multiline
              rows={3}
            />
          </Box>

          <Divider />

          {/* OpenGraph Tags */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Open Graph (Social Media)
            </Typography>
            <TextField
              fullWidth
              label="OG Title"
              name="ogTitle"
              value={roleData.ogTitle}
              onChange={handleInputChange}
              placeholder="Title when shared on social media"
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="OG Description"
              name="ogDescription"
              value={roleData.ogDescription}
              onChange={handleInputChange}
              placeholder="Description when shared on social media"
              size="small"
              multiline
              rows={2}
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="OG Image URL"
              name="ogImage"
              value={roleData.ogImage}
              onChange={handleInputChange}
              placeholder="https://example.com/image.jpg"
              size="small"
            />
          </Box>

          <Divider />

          {/* Twitter Tags */}
          {/* <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Twitter Card
            </Typography>
            <TextField
              fullWidth
              label="Twitter Title"
              name="twitterTitle"
              value={roleData.twitterTitle}
              onChange={handleInputChange}
              placeholder="Title for Twitter"
              size="small"
              sx={{ mb: 1 }}
            />
            <TextField
              fullWidth
              label="Twitter Description"
              name="twitterDescription"
              value={roleData.twitterDescription}
              onChange={handleInputChange}
              placeholder="Description for Twitter"
              size="small"
              multiline
              rows={2}
            />
          </Box> */}

          <Alert severity="info">
            <Typography variant="caption">
              <strong>Supported Languages:</strong> English, हिंदी, தமிழ், తెలుగు, ಕನ್ನಡ, বাংলা, ગુજરાતી, मराठी
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          {editingRole ? "Update Role" : "Create Role"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RoleManager;
